import React, { useCallback, useState } from "react";
import { FileText, FolderOpen, UploadCloud, Clock } from "lucide-react";

interface RecentFile {
  path: string;
  name: string;
}

interface EmptyStateProps {
  recent: RecentFile[];
  onPickFile: () => void;
  onOpenFileObject: (file: File) => void;
  onOpenRecent: (path: string) => void;
}

/** Launch / no-document screen: drop zone + recent files. No marketing. */
const EmptyState: React.FC<EmptyStateProps> = ({
  recent,
  onPickFile,
  onOpenFileObject,
  onOpenRecent,
}) => {
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      // Stop the drop from also reaching the window-level handler in
      // DesktopApp, which would otherwise open the same file a second time.
      e.stopPropagation();
      setDragging(false);
      const file = Array.from(e.dataTransfer.files).find((f) =>
        f.name.toLowerCase().endsWith(".pdf"),
      );
      if (file) onOpenFileObject(file);
    },
    [onOpenFileObject],
  );

  return (
    <div className="empty-state">
      <div
        className={`empty-drop ${dragging ? "empty-drop-active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <UploadCloud size={56} className="empty-icon" />
        <h1 className="empty-title">Open a PDF</h1>
        <p className="empty-sub">Drag a file here, or choose one from your computer.</p>
        <button className="empty-open-btn" onClick={onPickFile}>
          <FolderOpen size={18} />
          Open PDF
        </button>
      </div>

      {recent.length > 0 && (
        <div className="empty-recent">
          <div className="empty-recent-head">
            <Clock size={16} />
            <span>Recent</span>
          </div>
          <ul className="empty-recent-list">
            {recent.slice(0, 8).map((f) => (
              <li key={f.path}>
                <button className="empty-recent-item" onClick={() => onOpenRecent(f.path)}>
                  <FileText size={16} />
                  <span className="empty-recent-name">{f.name}</span>
                  <span className="empty-recent-path">{f.path}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EmptyState;
